import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTwitter, FiRefreshCw, FiShare2, FiChevronDown, FiChevronUp, FiHeart, FiMessageCircle, FiRepeat, FiBookmark } = FiIcons;

const TrumpTruthFeed = ({ relatedNewsIndex = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedPost, setSelectedPost] = useState(0);
  
  // Mock Truth Social posts
  const truthPosts = [
    {
      id: 1,
      content: "The Border is BROKEN! My upcoming speech will outline how we'll FIX it on Day One. America FIRST means Americans are SAFE!",
      timestamp: "2 hours ago",
      likes: 245782,
      replies: 32891,
      retruths: 78934,
      related: "President Trump Announces Major Policy Speech on Immigration",
      isHighlighted: true
    },
    {
      id: 2,
      content: "Big WIN for our Second Amendment! SCOTUS upholds our Constitutional Rights against the Radical Left's agenda. Many people are saying this is HUGE!",
      timestamp: "4 hours ago",
      likes: 187429,
      replies: 25760,
      retruths: 64531,
      related: "Supreme Court Rules 6-3 in Favor of Second Amendment Rights",
      isHighlighted: false
    },
    {
      id: 3,
      content: "American Energy DOMINANCE is coming back! We'll be energy independent again - no more BEGGING other countries. Lower prices, more JOBS!",
      timestamp: "Yesterday",
      likes: 203567,
      replies: 18942,
      retruths: 59873,
      related: "GOP Introduces New Bill to Protect American Energy Independence",
      isHighlighted: false
    },
    {
      id: 4,
      content: "Republicans raising RECORD amounts! The Silent Majority is speaking with their wallets. Red Wave Coming! MAGA!",
      timestamp: "2 days ago",
      likes: 198745,
      replies: 17851,
      retruths: 52146,
      related: "Republican Fundraising Surges Ahead of Upcoming Elections",
      isHighlighted: false
    }
  ];

  // Set the selected post based on related news
  useEffect(() => {
    setSelectedPost(relatedNewsIndex);
  }, [relatedNewsIndex]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    
    // Simulate refreshing
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };

  const handleShare = (postId) => {
    if (navigator.share) {
      navigator.share({
        title: "Trump's Truth Social Post",
        text: truthPosts.find(post => post.id === postId)?.content || "Check out President Trump's latest Truth Social posts!",
        url: window.location.href,
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const shareUrl = window.location.href;
      navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard!');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num;
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-patriot-red text-white">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <SafeIcon icon={FiTwitter} className="h-5 w-5 text-patriot-red" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Trump Truth Social</h2>
              <p className="text-xs text-white text-opacity-75">@realDonaldTrump</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={handleRefresh}
              className="p-2 hover:bg-patriot-red-dark rounded-full transition-colors duration-200"
              disabled={isRefreshing}
              aria-label="Refresh feed"
            >
              <SafeIcon 
                icon={FiRefreshCw} 
                className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} 
              />
            </button>
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-patriot-red-dark rounded-full transition-colors duration-200"
              aria-label={isExpanded ? "Collapse feed" : "Expand feed"}
            >
              <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Feed Content */}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="relative">
              {/* Loading overlay */}
              {isRefreshing && (
                <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                  <div className="p-4 rounded-full bg-patriot-red bg-opacity-10">
                    <SafeIcon icon={FiRefreshCw} className="h-8 w-8 text-patriot-red animate-spin" />
                  </div>
                </div>
              )}
              
              {/* Posts List */}
              <div className="p-4">
                {/* Selected/Featured Post */}
                <div className={`mb-4 p-4 rounded-lg ${truthPosts[selectedPost].isHighlighted ? 'bg-patriot-red bg-opacity-5 border border-patriot-red border-opacity-20' : 'bg-gray-50'}`}>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <img 
                        src="https://images.unsplash.com/photo-1580477667995-2b94f01c9516?w=150&h=150&fit=crop&crop=face" 
                        alt="Donald Trump" 
                        className="w-12 h-12 rounded-full border-2 border-patriot-red"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-900">Donald J. Trump</p>
                          <p className="text-sm text-gray-500">@realDonaldTrump · {truthPosts[selectedPost].timestamp}</p>
                        </div>
                        {truthPosts[selectedPost].isHighlighted && (
                          <span className="bg-patriot-red text-white text-xs px-2 py-1 rounded-full">
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-gray-800 text-base leading-relaxed">
                        {truthPosts[selectedPost].content}
                      </p>
                      {truthPosts[selectedPost].related && (
                        <div className="mt-2 text-xs text-patriot-red">
                          Related to: {truthPosts[selectedPost].related}
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-between text-gray-500">
                        <button className="flex items-center space-x-1 hover:text-patriot-red transition-colors">
                          <SafeIcon icon={FiMessageCircle} className="h-4 w-4" />
                          <span className="text-xs">{formatNumber(truthPosts[selectedPost].replies)}</span>
                        </button>
                        <button className="flex items-center space-x-1 hover:text-green-600 transition-colors">
                          <SafeIcon icon={FiRepeat} className="h-4 w-4" />
                          <span className="text-xs">{formatNumber(truthPosts[selectedPost].retruths)}</span>
                        </button>
                        <button className="flex items-center space-x-1 hover:text-red-500 transition-colors">
                          <SafeIcon icon={FiHeart} className="h-4 w-4" />
                          <span className="text-xs">{formatNumber(truthPosts[selectedPost].likes)}</span>
                        </button>
                        <button 
                          onClick={() => handleShare(truthPosts[selectedPost].id)}
                          className="flex items-center space-x-1 hover:text-patriot-blue transition-colors"
                        >
                          <SafeIcon icon={FiShare2} className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Other Posts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {truthPosts.filter((_, index) => index !== selectedPost).map((post) => (
                    <div 
                      key={post.id} 
                      className="p-3 border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg"
                      onClick={() => setSelectedPost(truthPosts.findIndex(p => p.id === post.id))}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <img 
                            src="https://images.unsplash.com/photo-1580477667995-2b94f01c9516?w=150&h=150&fit=crop&crop=face" 
                            alt="Donald Trump" 
                            className="w-10 h-10 rounded-full"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-bold text-gray-900">Donald J. Trump</p>
                              <p className="text-xs text-gray-500">@realDonaldTrump · {post.timestamp}</p>
                            </div>
                          </div>
                          <p className="mt-1 text-gray-800 text-sm line-clamp-2">
                            {post.content}
                          </p>
                          <div className="mt-2 flex items-center space-x-4 text-gray-500">
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiMessageCircle} className="h-3 w-3" />
                              <span className="text-xs">{formatNumber(post.replies)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiHeart} className="h-3 w-3" />
                              <span className="text-xs">{formatNumber(post.likes)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 border-t border-gray-100 text-center">
              <p className="text-sm text-gray-500">
                Follow President Trump on <a href="https://truthsocial.com/@realDonaldTrump" target="_blank" rel="noopener noreferrer" className="text-patriot-red hover:underline">Truth Social</a>
              </p>
            </div>
          </motion.div>
        )}
        
        {/* Collapsed State */}
        {!isExpanded && (
          <div className="p-4 text-center">
            <p className="text-sm text-gray-600">Tap to see President Trump's latest posts</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TrumpTruthFeed;